'use client';

import * as React from 'react';
import { motion, type HTMLMotionProps, type Transition } from 'motion/react';

import { SlidingNumber, type SlidingNumberProps } from '../sliding-number';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

type CounterProps = HTMLMotionProps<'div'> & {
  number: number;
  setNumber: (number: number) => void;
  slidingNumberProps?: Omit<SlidingNumberProps, 'number'>;
  buttonProps?: Omit<React.ComponentProps<typeof Button>, 'onClick'>;
  transition?: Transition;
  suffix?: string;
  children?: React.ReactNode;
};

function Counter({
  number,
  setNumber,
  className,
  slidingNumberProps,
  buttonProps,
  suffix,
  transition = { type: 'spring', bounce: 0, stiffness: 300, damping: 30 },
  children,
  ...props
}: CounterProps) {
  return (
    <motion.div
      data-slot="counter"
      layout
      transition={transition}
      className={cn(
        'tw:flex tw:items-center tw:gap-x-2 tw:p-1 tw:rounded-xl',
        className,
      )}
      {...props}
    >
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          size="icon"
          {...buttonProps}
          onClick={() => setNumber(number - 1)}
          className={cn(
            'tw:bg-neutral-100 tw:dark:bg-neutral-950 tw:hover:bg-neutral-300 tw:dark:hover:bg-neutral-950/70 tw:text-neutral-950 tw:dark:text-white tw:text-2xl tw:font-light tw:pb-[3px]',
            buttonProps?.className,
            buttonProps?.disabled && 'tw:opacity-50 tw:cursor-not-allowed',
          )}
        >
          -
        </Button>
      </motion.div>

      <SlidingNumber
        number={number}
        suffix={suffix}
        {...slidingNumberProps}
        className={cn(
          'tw:font-semibold tw:text-xl tw:justify-center',
          number < 1000 && 'tw:w-[3ch]',
          slidingNumberProps?.className,
        )}
      />
      {children}

      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          size="icon"
          {...buttonProps}
          onClick={() => setNumber(number + 1)}
          className={cn(
            'tw:bg-neutral-100 tw:dark:bg-neutral-950 tw:hover:bg-neutral-300 tw:dark:hover:bg-neutral-950/70 tw:text-neutral-950 tw:dark:text-white tw:text-2xl tw:font-light tw:pb-[3px]',
            buttonProps?.className,
          )}
        >
          +
        </Button>
      </motion.div>
    </motion.div>
  );
}

export { Counter, type CounterProps };
